import { faPencil, faTimes, faToggleOff, faToggleOn } from "@fortawesome/free-solid-svg-icons";
import { DataTable } from "@ventumsoftware/react-toolkit";
import React, { type FC } from "react";
import { useApi } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";
import { TablePlaceholder } from "../../components/data/TablePlaceholder";
import { Button } from "../../components/form/Button";
import { Loading } from "../../components/Loading";
import { useModal } from "../../components/layout/ModalProvider";
import { Paragraph } from "../../components/Paragraph";
import { AddAssessorModal } from "../../modals/AddAssessorModal";
import { EditAssessorModal } from "../../modals/EditAssessorModal";

export const SettingsAssessorsPage: FC = () => {
  const { assessors, loading, refresh } = useApi();
  const { updateAssessor, destroyAssessor } = useApiCall();
  const addAssessorModal = useModal({
    title: "Assessor toevoegen",
    body: <AddAssessorModal />,
    size: "md",
  });
  const editAssessorModal = useModal({
    title: "Assessor bewerken",
    body: (params) => {
      const assessor = assessors.find((a) => a.id === params?.[0]);
      if (!assessor) {
        return <>Assessor bestaat niet</>;
      }
      return <EditAssessorModal assessor={assessor} onFinish={() => refresh.assessors(false)} />;
    },
    size: "md",
  });
  return (
    <ContentContainer size={"lg"}>
      <div className={"flex items-center justify-between"}>
        <PageHeader>Assessor Instellingen</PageHeader>
        <div>
          <Button
            type={"primary"}
            size={"md"}
            text={"Assessor toevoegen"}
            onClick={() => addAssessorModal.open()}
          />
        </div>
      </div>
      <div className={"-mt-2 max-w-(--breakpoint-lg)"}>
        <Paragraph>
          De assessors die hier ingesteld staan zijn beschikbaar in de app om toe te wijzen aan
          actieve assessments. De assessor naam komt terug in een deel van de rapportages.
        </Paragraph>
      </div>
      <Loading loading={loading}>
        {assessors.length === 0 ? (
          <TablePlaceholder>Geen assessors in in systeem</TablePlaceholder>
        ) : (
          <DataTable
            keyProperty={"id"}
            data={assessors}
            pageSize={50}
            columns={[
              {
                header: "Naam",
                sort: true,
                property: "last_name",
                transform: (_, row) => `${row.first_name} ${row.last_name}`,
              },
              {
                header: "Ingeschakeld",
                property: "options",
                transform: (_, r) => (r.isEnabled() ? "Yes" : "No"),
              },
              {
                header: "Contactgegevens",
                property: "id",
                transform: (_, row) => {
                  if (row.company || row.email || row.phone) {
                    return (
                      <div
                        className={
                          "flex flex-col space-y-1 text-sm border border-slate-200 rounded-sm px-2 py-1"
                        }
                      >
                        {row.company && <div>{row.company}</div>}
                        {row.email && <div>{row.email}</div>}
                        {row.phone && <div>{row.phone}</div>}
                      </div>
                    );
                  }
                  return <></>;
                },
              },
            ]}
            onClickActions={[
              {
                icon: () => faPencil,
                text: () => "Bewerk",
                onClick: async (row) => {
                  editAssessorModal.open([row.id]);
                },
              },
              {
                icon: (r) => (r.isEnabled() ? faToggleOn : faToggleOff),
                text: (r) => (r.isEnabled() ? "Disable" : "Enable"),
                onClick: async (row) => {
                  await updateAssessor(row, { options: { enabled: !row.isEnabled() } });
                  await refresh.assessors(true);
                },
              },
              {
                icon: faTimes,
                text: "Verwijderen",
                variant: "danger",
                onClick: async (row) => {
                  const sure = confirm("Weet je zeker dat je deze assessor wilt verwijderen?");
                  if (sure) {
                    await destroyAssessor(row.id);
                    await refresh.assessors(true);
                  }
                },
              },
            ]}
          />
        )}
      </Loading>
    </ContentContainer>
  );
};
